import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import './FeaturesHighlightAdmin.css';

const FeaturesHighlightAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleFeatureUpdate = (index, field, value) => {
    updateContent(`${contentPath}.features.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddFeature = () => {
    const features = sectionData?.features || [];
    const newFeature = {
      icon: 'fas fa-certificate',
      photo: '',
      title: '',
      description: '',
      stat: '',
      statDesc: ''
    };

    updateContent(`${contentPath}.features`, [...features, newFeature]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteFeature = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fonctionnalité ?')) {
      const features = sectionData?.features || [];
      const updatedFeatures = features.filter((_, i) => i !== index);
      updateContent(`${contentPath}.features`, updatedFeatures);
      setSectionData(getContent(contentPath));
    }
  };

  if (!sectionData) {
    return (
      <div className="features-highlight-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="features-highlight-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-medal"></i>
            Fonctionnalités en vedette
          </h2>
          <p>Gérez les fonctionnalités en vedette de DentoXcellence</p>
        </div>
        <button 
          className="btn-add-feature"
          onClick={handleAddFeature}
        >
          <i className="fas fa-plus"></i>
          Ajouter une fonctionnalité
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Pourquoi Choisir DentoXcellence ?"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="L'excellence reconnue en formation dentaire..."
            rows={2}
          />
        </div>
      </div>

      {/* Features List */}
      <div className="features-list-admin">
        {(sectionData.features || []).map((feature, index) => (
          <div key={index} className="feature-item-admin">
            <div className="feature-header">
              <div className="feature-title-area">
                <h3>
                  <i className="fas fa-certificate"></i>
                  Fonctionnalité {index + 1}
                </h3>
              </div>
              <div className="feature-actions">
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteFeature(index)}
                  title="Supprimer cette fonctionnalité"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Clean Horizontal Form Fields */}
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Icône (optionnel, utilisé si pas de photo)</label>
                <div className="icon-selector">
                  <input
                    type="text"
                    value={feature.icon || ''}
                    onChange={(e) => handleFeatureUpdate(index, 'icon', e.target.value)}
                    placeholder="fas fa-certificate"
                  />
                  <div className="icon-preview">
                    <i className={feature.icon || 'fas fa-certificate'}></i>
                  </div>
                </div>
                <div className="icon-quick-select">
                  {[
                    { icon: 'fas fa-tooth', label: 'Dent' },
                    { icon: 'fas fa-user-md', label: 'Médecin' },
                    { icon: 'fas fa-graduation-cap', label: 'Formation' },
                    { icon: 'fas fa-certificate', label: 'Certificat' },
                    { icon: 'fas fa-award', label: 'Award' },
                    { icon: 'fas fa-medal', label: 'Médaille' },
                    { icon: 'fas fa-heart', label: 'Cœur' },
                    { icon: 'fas fa-briefcase-medical', label: 'Mallette' },
                    { icon: 'fas fa-microscope', label: 'Microscope' },
                    { icon: 'fas fa-stethoscope', label: 'Stéthoscope' },
                    { icon: 'fas fa-user-friends', label: 'Équipe' },
                    { icon: 'fas fa-globe', label: 'Globe' },
                    { icon: 'fas fa-star', label: 'Étoile' },
                    { icon: 'fas fa-lightbulb', label: 'Idée' },
                    { icon: 'fas fa-check-circle', label: 'Check' },
                    { icon: 'fas fa-shield-alt', label: 'Bouclier' }
                  ].map((iconOption, iconIndex) => (
                    <button
                      key={iconIndex}
                      type="button"
                      className={`icon-option ${feature.icon === iconOption.icon ? 'active' : ''}`}
                      onClick={() => handleFeatureUpdate(index, 'icon', iconOption.icon)}
                      title={iconOption.label}
                    >
                      <i className={iconOption.icon}></i>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group full-width">
                <label>Photo de la fonctionnalité</label>
                <ImageUpload
                  value={feature.photo}
                  onChange={(url) => handleFeatureUpdate(index, 'photo', url)}
                  label="Télécharger une photo"
                />
              </div>

              <div className="form-group full-width">
                <label>Titre</label>
                <input
                  type="text"
                  value={feature.title || ''}
                  onChange={(e) => handleFeatureUpdate(index, 'title', e.target.value)}
                  placeholder="Formation Premium"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={feature.description || ''}
                  onChange={(e) => handleFeatureUpdate(index, 'description', e.target.value)}
                  placeholder="Des programmes de formation de haute qualité..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Statistique</label>
                <input
                  type="text"
                  value={feature.stat || ''}
                  onChange={(e) => handleFeatureUpdate(index, 'stat', e.target.value)}
                  placeholder="98%"
                />
              </div>

              <div className="form-group">
                <label>Description de la statistique</label>
                <input
                  type="text"
                  value={feature.statDesc || ''}
                  onChange={(e) => handleFeatureUpdate(index, 'statDesc', e.target.value)}
                  placeholder="Taux de satisfaction"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!sectionData.features || sectionData.features.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-medal"></i>
          </div>
          <h3>Aucune fonctionnalité</h3>
          <p>Commencez par ajouter votre première fonctionnalité.</p>
          <button className="btn-add-first" onClick={handleAddFeature}>
            <i className="fas fa-plus"></i>
            Ajouter la première fonctionnalité
          </button>
        </div>
      )}
    </div>
  );
};

export default FeaturesHighlightAdmin;

